import { useEffect, useState } from "react";
import { getKatlasStatus } from "../lib/api";
import { ShieldCheck, CircleAlert, CircleDashed, Hash } from "lucide-react";

function short(hash) {
    if (!hash) return "—";
    if (hash.length <= 18) return hash;
    return `${hash.slice(0, 12)}…${hash.slice(-6)}`;
}

export function KatlasNodeBadge() {
    const [status, setStatus] = useState(null);
    useEffect(() => {
        let alive = true;
        getKatlasStatus()
            .then((s) => alive && setStatus(s))
            .catch(() => alive && setStatus({ available: false, error: "unreachable" }));
        return () => {
            alive = false;
        };
    }, []);

    if (!status) {
        return (
            <span
                className="inline-flex items-center gap-2 text-xs font-mono text-slate-500"
                data-testid="katlas-node-badge-loading"
            >
                <CircleDashed size={14} strokeWidth={1.5} /> Probing KATLAS node…
            </span>
        );
    }

    const live = status.available;
    return (
        <span
            className="inline-flex items-center gap-2 text-xs font-mono"
            data-testid="katlas-node-badge"
        >
            <span
                className="dot"
                style={{ background: live ? "#10b981" : "#f59e0b" }}
            />
            <span className="uppercase tracking-[0.18em] text-slate-600">
                KATLAS node
            </span>
            <span className="text-slate-900 font-semibold">
                {live ? "live" : status.has_token ? "unreachable" : "pending live key"}
            </span>
        </span>
    );
}

function receiptModeLabel(receipt) {
    if (receipt?.live) return "live · signed";
    if (receipt?.mode === "simulation") return "synthetic";
    return "unsigned · pending key";
}

export default function KatlasProofCard({ receipt }) {
    const live = receipt?.live;
    const status = receipt?.verification_status || "—";

    return (
        <div
            className="terminal rounded-sm p-6 lg:p-7"
            data-testid="katlas-proof-card"
        >
            <div className="flex items-center justify-between mb-5">
                <div className="flex items-center gap-3">
                    <ShieldCheck
                        size={18}
                        strokeWidth={1.5}
                        className={live ? "text-emerald-400" : "text-amber-400"}
                    />
                    <span className="font-mono text-[11px] tracking-[0.22em] uppercase text-slate-300">
                        KATLAS Proof Receipt
                    </span>
                </div>
                <span
                    className={`font-mono text-[11px] tracking-[0.18em] uppercase ${
                        live ? "ok" : "warn"
                    }`}
                    data-testid="katlas-proof-mode"
                >
                    {receiptModeLabel(receipt)}
                </span>
            </div>

            <dl className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-3 font-mono text-sm">
                <Row label="Node">
                    <span className={live ? "ok" : "warn"}>
                        {live ? "active" : status}
                    </span>
                </Row>
                <Row label="Verification">{status}</Row>
                <Row label="Receipt hash">
                    <span className="inline-flex items-center gap-1">
                        <Hash size={12} strokeWidth={1.5} />
                        <span data-testid="katlas-receipt-hash">
                            {short(receipt?.receipt_hash)}
                        </span>
                    </span>
                </Row>
                <Row label="Payload hash">{short(receipt?.payload_hash)}</Row>
                <Row label="Node timestamp">{receipt?.node_timestamp || "—"}</Row>
                <Row label="Signer">{receipt?.signer || "—"}</Row>
                <Row label="Attestation">{receipt?.attestation_ref || "—"}</Row>
                <Row label="Scenario ID">{receipt?.scenario_id || "—"}</Row>
                <Row label="Decision ID">{receipt?.decision_id || "—"}</Row>
            </dl>

            <div className="mt-6 grid grid-cols-1 md:grid-cols-3 gap-4 text-xs">
                <Block label="Shared evidence (refs)" items={receipt?.shared_evidence_refs} tone="ok" />
                <Block label="Withheld evidence" items={receipt?.withheld_evidence} tone="warn" />
                <Block label="Not sent to node" items={receipt?.not_sent_to_node} tone="err" />
            </div>

            <div className="mt-6 pt-4 border-t border-slate-700/60 grid grid-cols-1 md:grid-cols-3 gap-4 text-xs">
                <CarBlock label="Custody" value={receipt?.car_mapping?.custody} />
                <CarBlock label="Authority" value={receipt?.car_mapping?.authority} />
                <CarBlock label="Receipts" value={receipt?.car_mapping?.receipts} />
            </div>

            {receipt?.error && (
                <div className="mt-5 text-xs font-mono text-amber-300 flex items-start gap-2">
                    <CircleAlert size={14} strokeWidth={1.5} />
                    <span>{receipt.error}</span>
                </div>
            )}
        </div>
    );
}

function Row({ label, children }) {
    return (
        <div className="flex items-center justify-between gap-4 border-b border-slate-800/70 pb-2">
            <dt className="text-slate-400 uppercase tracking-[0.18em] text-[10px]">
                {label}
            </dt>
            <dd className="text-slate-100 truncate text-right">{children}</dd>
        </div>
    );
}

function Block({ label, items, tone }) {
    const color =
        tone === "ok"
            ? "text-emerald-300"
            : tone === "warn"
              ? "text-amber-300"
              : "text-rose-300";
    return (
        <div>
            <div className={`uppercase tracking-[0.18em] text-[10px] ${color} mb-2`}>
                {label}
            </div>
            <ul className="space-y-1 text-slate-200">
                {(items || []).map((it) => (
                    <li key={it} className="flex gap-2">
                        <span className="text-slate-500">·</span>
                        <span>{it}</span>
                    </li>
                ))}
                {(!items || items.length === 0) && (
                    <li className="text-slate-500 italic">—</li>
                )}
            </ul>
        </div>
    );
}

function CarBlock({ label, value }) {
    return (
        <div>
            <div className="uppercase tracking-[0.18em] text-[10px] text-slate-400 mb-2">
                {label}
            </div>
            <p className="text-slate-200 leading-relaxed">{value || "—"}</p>
        </div>
    );
}
